import { Provider } from 'react-redux';
import { Suspense, lazy } from 'react';
import { persistor, store } from './redux/store';
import { RequireAuth } from './routes/RequireAuth';
import { RequireLogin } from './routes/RequireLogin';
import SimpleRootLayout from './routes/SimpleRootLayout';
import SpinnerLoader from './components/ui/SpinnerLoader';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { loggedRouters, staticRouters } from './routes/RouterElements';
import ErrorBoundary, { RouterErrorBoundary } from './routes/ErrorBoundary';
import { Route, RouterProvider, createBrowserRouter, createRoutesFromElements } from 'react-router-dom';

// react lazy pages

const LangGuard = lazy(() => import('./routes/LangGuard'));
const HomeRouter = lazy(() => import('./routes/HomeRouter'));
const UserRouter = lazy(() => import('./routes/UserRouter'));
const RootLayout = lazy(() => import('./routes/RootLayout'));
const UserLogin = lazy(() => import('./pages/auth/UserLogin'));
const UserRegister = lazy(() => import('./pages/auth/UserRegister'));
const UserValidate = lazy(() => import('./pages/dashboard/users/UserValidate'));

function App() {

  // react query options

  const queryClient = new QueryClient();

  // routers options

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route path='/' element={
        <ErrorBoundary>
          <RootLayout />
        </ErrorBoundary>
      } errorElement={<RouterErrorBoundary />}>
        <Route index element={<HomeRouter />} />
        <Route path='register' element={<UserRegister />} />
        <Route path='login' element={
          <RequireLogin>
            <UserLogin />
          </RequireLogin>
        } />
        <Route path='users/validate' element={<SimpleRootLayout />}>
          <Route path=':id' element={
            <RequireLogin>
              <UserValidate />
            </RequireLogin>
          } />
        </Route>

        {staticRouters?.map((item, int) => (
          <Route key={int} path={item?.path} element={item?.element} />
        ))}

        <Route element={
          <RequireAuth>
            <UserRouter />
          </RequireAuth>
        }>
          {loggedRouters?.map((item, int) => (
            <Route key={int} path={item?.path}>
              <Route index element={item?.index} />
              {item?.childrens?.map((child, count) => (
                <Route key={count} path={child?.childPath} element={child?.element} />
              ))}
            </Route>
          ))}
        </Route>

        <Route path=':lang' element={<LangGuard />}>
          <Route index element={<HomeRouter />} />
          <Route path='register' element={<UserRegister />} />
          <Route path='login' element={
            <RequireLogin>
              <UserLogin />
            </RequireLogin>
          } />
          <Route path='users/validate' element={<SimpleRootLayout />}>
            <Route path=':id' element={
              <RequireLogin>
                <UserValidate />
              </RequireLogin>
            } />
          </Route>

          {staticRouters?.map((item, int) => (
            <Route key={int} path={item?.path} element={item?.element} />
          ))}

          <Route element={
            <RequireAuth>
              <UserRouter />
            </RequireAuth>
          }>
            {loggedRouters?.map((item, int) => (
              <Route key={int} path={item?.path}>
                <Route index element={item?.index} />
                {item?.childrens?.map((child, count) => (
                  <Route key={count} path={child?.childPath} element={child?.element} />
                ))}
              </Route>
            ))}
          </Route>
        </Route>
      </Route>
    )
  );

  return (
    <Suspense fallback={<SpinnerLoader />}>
      <Provider store={store}>
        <PersistGate persistor={persistor}>
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
          </QueryClientProvider>
        </PersistGate>
      </Provider>
    </Suspense>
  )
}

export default App;